import { useEffect, useRef } from 'react';

/* =============================================================================
   useChromeThemeColor — PWA titlebar follows the portal chrome (ADDITIVE)
   -----------------------------------------------------------------------------
   The installed PWA's window titlebar is painted from <meta name="theme-color">.
   The portal chrome is navy by default and recolours under the Comfort themes,
   so a hardcoded navy meta leaves a Comfort user with a navy titlebar sitting
   above a cream bar. This hook keeps the two in sync.

   The colour is READ FROM COMPUTED STYLE rather than looked up in a table. The
   shell element carries both `data-theme` and the --chrome-* token set, so
   getComputedStyle resolves the same value the bar is actually painted with —
   it cannot drift from PortalLayout.css / comfort-portal.css, and adding a
   fourth theme stays the documented three-file change (ThemePicker.js:10-11).

   Read --chrome-bg, never getComputedStyle(topbar).backgroundColor: the topbar
   transitions background-color, so that would return mid-transition rgb()
   intermediates. Unregistered custom properties don't interpolate, so
   --chrome-bg switches discretely.

   Owned imperatively rather than through react-helmet-async: the value is only
   knowable after commit, and helmet doesn't dedupe against the static tag in
   index.html, so routing it through helmet would guarantee two competing tags.

   NOTE: index.html deliberately carries exactly one theme-color tag. Browsers
   honour the last matching one, so re-adding a media-scoped variant there would
   silently shadow everything this hook writes.

   Not fixable here: manifest.json's static theme_color is what the OS paints for
   the splash screen and window frame BEFORE index.html is parsed, so a cold
   launch always flashes navy first.
   ============================================================================= */

const META_SELECTOR = 'meta[name="theme-color"]';

function metaTag() {
	if (typeof document === 'undefined') return null;
	return document.querySelector(META_SELECTOR);
}

// The default/marketing navy, captured once from the tag itself so the hex is
// never re-typed in JS. The fallback only applies if the tag is missing.
const BASELINE = metaTag()?.getAttribute('content') || '#384971';

// The colour the chrome is actually painted with, or '' if it can't be read.
function readChromeColor(el) {
	if (!el) return '';
	// getPropertyValue returns a leading space — trim is mandatory.
	return getComputedStyle(el).getPropertyValue('--chrome-bg').trim();
}

function paint(color) {
	const meta = metaTag();
	if (meta) meta.setAttribute('content', color || BASELINE);
}

/**
 * @param {React.RefObject<HTMLElement>} shellRef ref to the .portal-shell element
 * @param {string|null} appliedTheme the resolved data-theme value (null = default)
 */
export function useChromeThemeColor(shellRef, appliedTheme) {
	// Holds any pending restore scheduled by a previous cleanup, so the next
	// effect run can cancel it. A cleanup cannot cancel its own frame.
	const pendingRestore = useRef(0);

	useEffect(() => {
		if (typeof document === 'undefined') return undefined;

		// A restore queued by the outgoing shell is now stale — this shell is
		// mounted and authoritative.
		if (pendingRestore.current) {
			cancelAnimationFrame(pendingRestore.current);
			pendingRestore.current = 0;
		}

		paint(readChromeColor(shellRef.current));

		return () => {
			// Deliberately NOT an immediate restore. On a portal→portal swap
			// between the two shells (PortalLayout ↔ PortalSpaceChrome), React can
			// run this cleanup AFTER the incoming shell's effect, which would
			// strand the tag on the baseline navy. Re-reading the DOM on the next
			// frame is self-healing whichever order those run in: if any shell is
			// still mounted we take its colour, otherwise we're genuinely off the
			// portal and restore the baseline.
			pendingRestore.current = requestAnimationFrame(() => {
				pendingRestore.current = 0;
				const shell = document.querySelector('.portal-shell');
				paint(shell ? readChromeColor(shell) : BASELINE);
			});
		};
	}, [shellRef, appliedTheme]);
}

export default useChromeThemeColor;
